// flowPipeline — pure resolution of the per-flow dashboard pipeline OVERLAY
// (commodity_template_stages, mig 879). Kept framework-free + dependency-light
// so it unit-tests in the node Vitest env (see __tests__/flowPipeline.test.ts);
// the React wiring lives in useFlowPipeline() (src/hooks/useCommodityTemplates.ts).
//
// Overlay semantics: a process (a MODULES[].key) is HIDDEN when a row has
// is_enabled=FALSE, and POSITIONED within its category band when a row carries
// sort_order. A process with NO row inherits today's behaviour (shown iff the
// module entitlement permits it, natural array order). The overlay can only
// SUBTRACT/REORDER — never grant.

import type { FlowPipelineOverlay } from '@/modules/packhouse/flows/flowManifest'

// Raw commodity_template_stages row (snake_case, as PostgREST returns it).
export interface CommodityTemplateStageRow {
  flow_key:    string
  process_key: string
  is_enabled:  boolean
  sort_order:  number | null
}

// Resolved overlay for one flow. Plain Set/Map — a DERIVED value, never stored
// in the (persisted) React Query cache.
export interface FlowPipeline {
  hiddenSet: Set<string>
  orderMap:  Map<string, number>
}

function pipelineFromRows(forFlow: CommodityTemplateStageRow[]): FlowPipeline {
  const hiddenSet = new Set<string>()
  const orderMap = new Map<string, number>()
  for (const r of forFlow) {
    if (!r.is_enabled) hiddenSet.add(r.process_key)
    if (r.sort_order != null) orderMap.set(r.process_key, r.sort_order)
  }
  return { hiddenSet, orderMap }
}

function pipelineFromOverlay(overlay: FlowPipelineOverlay): FlowPipeline {
  return { hiddenSet: new Set(overlay.hidden), orderMap: new Map(Object.entries(overlay.order ?? {})) }
}

// Precedence: this flow's DB rows → the in-code `fallback` overlay. `rows` is
// undefined while loading / offline / when the table doesn't exist yet (a
// frontend-first deploy makes PostgREST throw) — all of which fall to the
// fallback, so the dashboard never blanks. An empty `fallback` ({hidden:[]}) =
// today's behaviour (nothing hidden, natural order).
export function resolveFlowPipeline(
  rows: CommodityTemplateStageRow[] | undefined,
  flowKey: string,
  fallback: FlowPipelineOverlay,
): FlowPipeline {
  const forFlow = (rows ?? []).filter(r => r.flow_key === flowKey)
  return forFlow.length > 0 ? pipelineFromRows(forFlow) : pipelineFromOverlay(fallback)
}

// Raw tenant_pipeline_overrides row (mig 885) — same shape as a stage row minus
// flow_key (the override is keyed by tenant, applied on top of whatever flow the
// tenant runs).
export interface TenantPipelineOverrideRow {
  process_key: string
  is_enabled:  boolean
  sort_order:  number | null
}

function applyOverrideRow(hiddenSet: Set<string>, orderMap: Map<string, number>, o: TenantPipelineOverrideRow): void {
  if (o.is_enabled) hiddenSet.delete(o.process_key)
  else hiddenSet.add(o.process_key)
  if (o.sort_order == null) orderMap.delete(o.process_key)
  else orderMap.set(o.process_key, o.sort_order)
}

// Merge a tenant's overrides OVER the resolved commodity default (P2c, mig 885).
// Row-level: each override row fully replaces the default for THAT process
// (is_enabled → show/hide; sort_order null → clear any default order). A process
// with no override row keeps the default. Returns `base` unchanged when there
// are no overrides (loading / offline / a tenant with none).
export function mergeTenantOverrides(
  base: FlowPipeline,
  overrides: TenantPipelineOverrideRow[] | undefined,
): FlowPipeline {
  if (!overrides?.length) return base
  const hiddenSet = new Set(base.hiddenSet)
  const orderMap = new Map(base.orderMap)
  for (const o of overrides) applyOverrideRow(hiddenSet, orderMap, o)
  return { hiddenSet, orderMap }
}

// A per-process draft cell from the tenant-override UI: shown? + an order string
// ('' = no explicit order).
export interface DraftCell { enabled: boolean; order: string }

// '' / blank → null; else the parsed integer.
function parseOrder(order: string): number | null {
  return order.trim() === '' ? null : Number(order)
}

// One process's deviation from the commodity default, or null when it matches
// (so the override stays SPARSE — only stored deviations; untouched processes
// inherit the default and any future change to it).
function deviationRow(processKey: string, cell: DraftCell, def: FlowPipeline): TenantPipelineOverrideRow | null {
  const defEnabled = !def.hiddenSet.has(processKey)
  const defOrder = def.orderMap.get(processKey) ?? null
  const order = parseOrder(cell.order)
  if (cell.enabled === defEnabled && order === defOrder) return null
  return { process_key: processKey, is_enabled: cell.enabled, sort_order: order }
}

// Diff the tenant's edited draft against the commodity DEFAULT pipeline → the
// sparse set of override rows to persist (what `set_tenant_pipeline` stores).
export function tenantOverrideDeviations(
  draft: Record<string, DraftCell>,
  def: FlowPipeline,
): TenantPipelineOverrideRow[] {
  const out: TenantPipelineOverrideRow[] = []
  for (const [processKey, cell] of Object.entries(draft)) {
    const row = deviationRow(processKey, cell, def)
    if (row) out.push(row)
  }
  return out
}
